/**
 * P4-N5B — SLICE C6 — Price-List Dataset Capability (R1)
 *
 * Frontend-only capability declaration for the Customer Portal
 * Price Lists surface. Distinct from TRANSACTION capability because
 * an Esso-USA price-list dataset does NOT exist in production even
 * though Esso-USA transactions do.
 *
 * Authoritative grounding:
 * - C6 Pre-Impl RCA — PRICE_LIST_CAPABILITY_MATRIX.md
 *   `/app/rca_reports/p4_n5b_c6_portal_price_lists_country_preimplementation_rca/`
 * - Backend report builders — `reports.build_customer_price_report`
 *   (Esso CA · Pipeline CA · PP CA) and
 *   `reports.build_petro_pass_us_customer_price_report` (PP US).
 *
 * ─── HARD INVARIANTS ─────────────────────────────────────────────
 * 1. Esso price-list capability is CANADA ONLY. Never widen to USA.
 * 2. Pipeline price-list capability is CANADA ONLY.
 * 3. Petro-Pass price-list capability is CANADA + USA.
 * 4. `priceListCountriesFor("")` returns [] — "All networks" has NO
 *    legal country combination and the Country control must be
 *    locked in that state (never emits an impossible wire pair).
 * 5. This module NEVER re-implements C1's transaction capability;
 *    reusing `_ledgerCountriesFor` on this page would incorrectly
 *    expose Esso-USA in the Country dropdown.
 */

export const PRICE_LIST_CAPABILITY = Object.freeze({
  esso:       Object.freeze(["CA"]),
  pipeline:   Object.freeze(["CA"]),
  petro_pass: Object.freeze(["CA", "US"]),
});

/**
 * Return the ordered list of country wire values ("CA", "US") that a
 * given canonical network supports for the price-list dataset.
 *
 * Empty string ("") — the "All networks" sentinel — returns [] because
 * there is no single backend endpoint that serves a cross-network
 * price-list; callers must lock the Country control in that state.
 * Unknown networks also return [] so the caller fails closed.
 */
export function priceListCountriesFor(network) {
  const supported = PRICE_LIST_CAPABILITY[network];
  if (!Array.isArray(supported)) return [];
  return supported.slice();
}
